// src/env.ts
var ENV_CONTRACT = [
  // Frontend-safe (VITE_ prefix)
  { key: "VITE_SUPABASE_URL", required: true, frontendSafe: true, description: "Supabase project URL" },
  { key: "VITE_SUPABASE_ANON_KEY", required: true, frontendSafe: true, description: "Supabase anonymous/public key" },
  { key: "VITE_APP_NAME", required: false, frontendSafe: true, description: "Application display name" },
  { key: "VITE_APP_ENV", required: false, frontendSafe: true, description: "Environment identifier (development/preview/production)" },
  { key: "VITE_OLIVIUM_MODE", required: false, frontendSafe: true, description: "OLIVIUM mode: demo or live" },
  { key: "VITE_SENTRY_DSN", required: false, frontendSafe: true, description: "Sentry DSN for browser error reporting" },
  // Server-only (no VITE_ prefix — never in client bundle)
  { key: "SUPABASE_SERVICE_ROLE_KEY", required: false, frontendSafe: false, description: "Supabase service role key \u2014 bypasses RLS" },
  { key: "STRIPE_SECRET_KEY", required: false, frontendSafe: false, description: "Stripe secret key for billing" },
  { key: "SLACK_BOT_TOKEN", required: false, frontendSafe: false, description: "Slack bot token for agent comms" },
  { key: "VERCEL_DEPLOY_HOOK", required: false, frontendSafe: false, description: "Vercel deploy hook URL" },
  { key: "RESEND_API_KEY", required: false, frontendSafe: false, description: "Resend API key for transactional email" },
  // Redis (memory/performance layer). Either REDIS_URL alone, or REDIS_HOST + extras.
  { key: "REDIS_URL", required: false, frontendSafe: false, description: "Full Redis connection URL (preferred for managed providers)" },
  { key: "REDIS_HOST", required: false, frontendSafe: false, description: "Redis host (used if REDIS_URL not set)" },
  { key: "REDIS_PORT", required: false, frontendSafe: false, description: "Redis port (defaults to 6379)" },
  { key: "REDIS_PASSWORD", required: false, frontendSafe: false, description: "Redis password" },
  { key: "REDIS_TLS", required: false, frontendSafe: false, description: 'Set to "true" to enable TLS' }
];
function validateEnv(env, context = "frontend") {
  const rules = context === "frontend" ? ENV_CONTRACT.filter((r) => r.frontendSafe) : ENV_CONTRACT;
  const missing = rules.filter((r) => r.required && !env[r.key]).map((r) => `  ${r.key} \u2014 ${r.description}`);
  if (missing.length > 0) {
    throw new Error(
      `[Square Code] Missing required environment variables (${context}):
${missing.join("\n")}

Copy .env.example to .env.local and fill in the values.`
    );
  }
}
var _injectedEnv = null;
function setFrontendEnv(source) {
  if (!source) {
    _injectedEnv = null;
    return;
  }
  const out = {};
  for (const [k, v] of Object.entries(source)) {
    if (v === void 0 || v === null) {
      out[k] = void 0;
    } else {
      out[k] = String(v);
    }
  }
  _injectedEnv = out;
}
function resolveSource() {
  if (_injectedEnv) return _injectedEnv;
  if (typeof process !== "undefined" && process.env) {
    return process.env;
  }
  return {};
}
function loadFrontendEnv() {
  const env = resolveSource();
  return {
    VITE_SUPABASE_URL: env.VITE_SUPABASE_URL || "",
    VITE_SUPABASE_ANON_KEY: env.VITE_SUPABASE_ANON_KEY || "",
    VITE_APP_NAME: env.VITE_APP_NAME || "OLIVIUM",
    VITE_APP_ENV: env.VITE_APP_ENV || "development",
    VITE_OLIVIUM_MODE: env.VITE_OLIVIUM_MODE || "demo",
    VITE_SENTRY_DSN: env.VITE_SENTRY_DSN
  };
}
function hasBrowserSupabaseConfig() {
  const env = resolveSource();
  return Boolean(env.VITE_SUPABASE_URL && env.VITE_SUPABASE_ANON_KEY);
}
var REQUIRED_BROWSER_AUTH_KEYS = [
  "VITE_SUPABASE_URL",
  "VITE_SUPABASE_ANON_KEY"
];
function getBrowserAuthReadiness() {
  const env = resolveSource();
  const missing = REQUIRED_BROWSER_AUTH_KEYS.filter((k) => !env[k]);
  return {
    ready: missing.length === 0,
    missing,
    appEnv: env.VITE_APP_ENV || "development",
    oliviumMode: env.VITE_OLIVIUM_MODE || "demo",
    supabaseUrlPresent: Boolean(env.VITE_SUPABASE_URL),
    supabaseAnonKeyPresent: Boolean(env.VITE_SUPABASE_ANON_KEY)
  };
}
function assertBrowserAuthConfig(source) {
  const env = source ? Object.fromEntries(
    Object.entries(source).map(([k, v]) => [k, v == null ? void 0 : String(v)])
  ) : resolveSource();
  const missing = REQUIRED_BROWSER_AUTH_KEYS.filter((k) => !env[k]);
  if (missing.length > 0) {
    throw new Error(
      `[Square Code] Browser auth config missing at build time: ${missing.join(", ")}. A production build must not ship a login page that can only return "Supabase client not available". Set these VITE_ variables in the Vercel Production build scope and redeploy. (Set SQUARE_ALLOW_UNCONFIGURED_AUTH_BUILD=1 only for an intentional demo-mode build.)`
    );
  }
}
function loadServerEnv() {
  const frontend = loadFrontendEnv();
  return {
    ...frontend,
    SUPABASE_SERVICE_ROLE_KEY: process.env.SUPABASE_SERVICE_ROLE_KEY || "",
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    SLACK_BOT_TOKEN: process.env.SLACK_BOT_TOKEN,
    VERCEL_DEPLOY_HOOK: process.env.VERCEL_DEPLOY_HOOK,
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    REDIS_URL: process.env.REDIS_URL,
    REDIS_HOST: process.env.REDIS_HOST,
    REDIS_PORT: process.env.REDIS_PORT,
    REDIS_PASSWORD: process.env.REDIS_PASSWORD,
    REDIS_TLS: process.env.REDIS_TLS
  };
}
function getEnvContract() {
  return ENV_CONTRACT;
}

export {
  validateEnv,
  setFrontendEnv,
  loadFrontendEnv,
  hasBrowserSupabaseConfig,
  REQUIRED_BROWSER_AUTH_KEYS,
  getBrowserAuthReadiness,
  assertBrowserAuthConfig,
  loadServerEnv,
  getEnvContract
};
